// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Loader2, ClipboardList, FileSignature, Sparkles, Receipt, FlaskConical, Stethoscope, ShieldAlert, Users, FileCheck2 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import type { TabKey } from '../CareRxPage'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import CancelVisitButton from './CancelVisitButton'
import Notice from '../../components/ui/Notice'
import AppButton from '../../components/ui/AppButton'

/**
 * Global CareRx dashboard — cross-domain rollup (plans, enrollment, clinical determinations, labs,
 * screening/holds, billing). Each operational card drills into that area's subdashboard.
 */
// D-CRX-33 R2: "View as" is a SAVED VIEW, never a permission (Q2 ruled) — it filters which kinds of
// work show; full scope stays with everyone in intake. A sick day never strands a queue.
const VIEWS: { key: string; label: string; kinds: string[] | null }[] = [
  { key: 'all', label: 'All work', kinds: null },
  { key: 'intake_a', label: 'Intake A', kinds: ['enrollment_review', 'pot_visit'] },
  { key: 'intake_b', label: 'Intake B', kinds: ['renewal', 'pot_visit'] },
  { key: 'pharmacist', label: 'Pharmacist', kinds: ['hold_review', 'enrollment_review'] },
  { key: 'nurse', label: 'Nurse', kinds: ['pot_visit'] },
  { key: 'approver', label: 'Approver', kinds: ['invoice_review'] },
]

export default function CareRxInsights({ onNavigate }: { onNavigate?: (tab: TabKey) => void }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getCareRxInsights>> | null>(null)
  const [pipe, setPipe] = useState<Awaited<ReturnType<typeof api.getCareRxPipeline>> | null>(null)
  const [view, setView] = useState('all')
  const [mineOnly, setMineOnly] = useState(false)
  const [myId, setMyId] = useState<string | null>(null)
  useEffect(() => { api.getAuthMe().then(m => setMyId(m.account_id ?? null)).catch(() => setMyId(null)) }, [])
  const [error, setError] = useState<string | null>(null)

  // Ticket 123qmzr1yyb: a busy database used to leave the CareRx landing page as one line of error
  // text with no way back but a full reload. It now says what happened and offers a retry.
  const [attempt, setAttempt] = useState(0)
  useEffect(() => { setError(null); api.getCareRxInsights().then(setData).catch(e => setError(friendlyError(e))) }, [attempt])
  useEffect(() => { api.getCareRxPipeline().then(setPipe).catch(() => setPipe(null)) }, [attempt])

  if (error && !data) return (
    <div style={{ padding: 30 }}>
      <Notice severity="warning">The dashboard could not be loaded. {error}</Notice>
      <AppButton variant="outlined" onClick={() => setAttempt(a => a + 1)}>Try again</AppButton>
    </div>
  )
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-mute)', display: 'flex', gap: 8, justifyContent: 'center' }}><Loader2 className="spin" size={20} /> Loading…</div>

  const sum = (arr: { count: number }[]) => arr.reduce((a, b) => a + b.count, 0)
  const go = (t: TabKey) => onNavigate ? () => onNavigate(t) : undefined
  const labsNeed = data.labs.expiring + data.labs.expired + data.labs.required_missing

  const kinds = VIEWS.find(v => v.key === view)?.kinds ?? null
  // ASP-CRX-35 / script I2: the assigned-to-me filter — claimed-by-me rows only when toggled.
  const myWork = (pipe?.my_work ?? []).filter(w => (!kinds || kinds.includes(w.kind))
    && (!mineOnly || (myId != null && String((w as any).assigned_to ?? '').toLowerCase() === myId.toLowerCase())))

  return (
    <div>
      {/* ASP-CRX-01 / spec §C: "upper mini-strip removed" — the pipeline band is GONE. The tiles
          below are the single count source; My work + Blockers stay. */}
      {pipe && (pipe as any).blockers && Object.values((pipe as any).blockers as Record<string, number>).some(v => v > 0) && (
        <div style={{ border: '1px solid var(--color-danger, #b3261e)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, background: 'var(--color-surface)' }}>
          {/* R3: Blockers — "empty when healthy": this panel does not render at zero. */}
          <span style={{ fontWeight: 600, color: 'var(--color-danger, #b3261e)', marginRight: 12 }}>Blockers</span>
          {/* ASP-CRX-48: exceptions-by-module summary — one line, grouped from the same counts */}
          <span style={{ display: 'block', width: '100%', fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 4 }}>
            {(() => {
              const b0 = (pipe as any).blockers as Record<string, number>
              const mod: Record<string, number> = {
                clinical: (b0.holds_active || 0) + (b0.pa_revoked_after_clearance || 0),
                reconciliation: b0.divergences_open || 0,
                staffing: (b0.cant_staff_open || 0) + (b0.credentials_expired || 0),
                'plans & signatures': (b0.signatures_aging || 0) + (b0.cadence_overdue || 0),
              }
              return Object.entries(mod).filter(([, v]) => v > 0).map(([k, v]) => `${k} ${v}`).join(' · ')
            })()}
          </span>
          {Object.entries((pipe as any).blockers as Record<string, number>).filter(([, v]) => v > 0).map(([k, v]) => (
            <span key={k} onClick={go(k === 'divergences_open' ? 'clinical' : k === 'credentials_expired' ? 'billing' : k === 'cant_staff_open' ? 'pot' : k === 'pa_revoked_after_clearance' ? 'enrollment' : k === 'holds_active' ? 'screening' : 'fulfillment')}
              style={{ marginRight: 14, cursor: 'pointer', fontSize: 13 }}>
              {/* Owner UI surf 2026-08-13: singular counts read singular ("1 divergence open"). */}
              <strong>{v}</strong> {v === 1 ? k.replace(/_/g, ' ').replace(/ies /, 'y ').replace(/([a-z])s (open|active|expired|aging|overdue)/, '$1 $2') : k.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
      {pipe && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 14, marginBottom: 18, background: 'var(--color-surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>My work</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setMineOnly(m => !m)} disabled={!myId}
                title={myId ? 'Show only items assigned to me' : 'Sign in with an account to filter'}
                style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--color-border)', background: mineOnly ? 'var(--color-accent-soft, #eef4f3)' : 'transparent', fontWeight: mineOnly ? 600 : 400 }}>
                Assigned to me
              </button>
              {VIEWS.map(v => (
                <button key={v.key} onClick={() => setView(v.key)}
                  style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--color-border)', background: view === v.key ? 'var(--color-accent-soft, #eef4f3)' : 'transparent', fontWeight: view === v.key ? 600 : 400 }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {myWork.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-mute)', padding: '6px 0' }}>Nothing waiting in this view.</div>
          ) : myWork.slice(0, 10).map(w => (
            <div key={w.kind + w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 4px', borderTop: '1px solid var(--color-border)', fontSize: 13 }}>
              <span onClick={go(w.tab as TabKey)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', flex: 1 }}>{w.label}<PatientLanguageBadge patientId={w.patient_id} /></span>
              <span style={{ flexShrink: 0, color: !w.at ? 'var(--color-text-mute)' : (Date.now() - new Date(w.at).getTime()) / 86400000 >= 7 ? '#b91c1c' : (Date.now() - new Date(w.at).getTime()) / 86400000 >= 3 ? '#b45309' : 'var(--color-text-mute)' }}>
                {/* ASP-CRX-05 / spec §C: relative ages, amber ≥3d / red ≥7d, no raw dates */}
                {w.kind.replace('_', ' ')}{w.at ? ` · ${Math.max(0, Math.floor((Date.now() - new Date(w.at).getTime()) / 86400000))}d` : ''}
              </span>
              {/* Go-live #5: a visit that will not happen leaves My work by being cancelled, not completed. */}
              {w.kind === 'pot_visit' && (
                <CancelVisitButton compact visitId={w.id} label={w.label}
                  onCancelled={() => { api.getCareRxPipeline().then(setPipe).catch(() => setPipe(null)) }} />
              )}
              {/* R4: item-level assignment — data, never permission; Claim/Release toggles */}
              {(w as any).assigned_to ? (
                <button onClick={async e => { e.stopPropagation(); try { await api.assignCareRxWork({ subject_type: w.kind, subject_id: w.id, action: 'release' }); setPipe(await api.getCareRxPipeline()) } catch { /* pre-252 tenants 409 */ } }}
                  title={'Assigned to a teammate'} /* ASP-CRX-22: no raw GUIDs, even sliced */
                  style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-accent-soft, #eef4f3)', cursor: 'pointer', flexShrink: 0 }}>
                  {String((w as any).assigned_to).slice(0, 6)}… ✕
                </button>
              ) : (
                <button onClick={async e => { e.stopPropagation(); try { await api.assignCareRxWork({ subject_type: w.kind, subject_id: w.id }); setPipe(await api.getCareRxPipeline()) } catch { /* pre-252 tenants 409 */ } onNavigate?.(w.tab as TabKey) /* ASP-CRX-07: Claim assigns AND lands you on the work */ }}
                  style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}>
                  Claim
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
      <Card icon={<Users size={16} />} title="Enrollment" big={sum(data.enrollment.by_status)} onClick={go('enrollment')}>
        <Breakdown rows={data.enrollment.by_status} />
      </Card>
      <Card icon={<FlaskConical size={16} />} title="Lab worklist" big={labsNeed} onClick={go('labs')}>
        <Row k="Expiring ≤30d" v={data.labs.expiring} />
        <Row k="Expired" v={data.labs.expired} />
        <Row k="Required missing" v={data.labs.required_missing} />
      </Card>
      <Card icon={<Stethoscope size={16} />} title="Clinical review" big={sum(data.clinical.by_status)} onClick={go('clinical')}>
        {/* ASP-CRX-02: STATUS ONLY — no medication names on the card */}
        <Breakdown rows={data.clinical.by_status} />
      </Card>
      <Card icon={<ShieldAlert size={16} />} title="Screening & hold" big={data.screening.active_holds} onClick={go('screening')}>
        <Row k="Active holds" v={data.screening.active_holds} />
        <Row k="Flagged screenings" v={data.screening.flagged_screenings} />
      </Card>
      <Card icon={<FileCheck2 size={16} />} title="Consent & fulfillment" big={data.fulfillment.open_consents + data.fulfillment.fills_in_flight} onClick={go('fulfillment')}>
        <Row k="Open consents" v={data.fulfillment.open_consents} />
        <Row k="Fills in flight" v={data.fulfillment.fills_in_flight} />
      </Card>
      <Card icon={<FileSignature size={16} />} title="Signatures" big={sum(data.assessments.by_signature)} onClick={go('assessments')}>
        {/* ASP-CRX-04 / spec §C: envelope states with user-facing labels */}
        <Breakdown rows={data.assessments.by_signature.map(r => ({ ...r, key: (
          { 'not sent': 'Not sent', sent: 'Sent — pending signature', delivered: 'Delivered — pending signature',
            signed: 'Signed', completed: 'Signed', voided: 'Revoked', declined: 'Declined' } as Record<string, string>
        )[r.key] ?? r.key }))} />
      </Card>
      <Card icon={<ClipboardList size={16} />} title="Plans of Care" big={sum(data.assessments.by_status)} onClick={go('assessments')}>
        <Breakdown rows={data.assessments.by_status} />
      </Card>
      <Card icon={<Receipt size={16} />} title="Billing" big={`$${data.billing.invoiced_total_usd.toFixed(2)}`} onClick={go('billing')}>
        <Row k="Agencies" v={data.billing.agencies} />
        <Row k="Invoices" v={data.billing.invoices} />
        <Row k="Unbilled visits" v={data.billing.unbilled_visits} />
      </Card>
      <Card icon={<Sparkles size={16} />} title="AI usage" big={data.ai.calls}>
        <Row k="Total cost" v={`$${data.ai.total_cost_usd.toFixed(2)}`} />
        <Breakdown rows={data.ai.by_phi_flag} />
      </Card>
    </div>
    </div>
  )
}

function Card({ icon, title, big, onClick, children }: { icon: React.ReactNode; title: string; big: React.ReactNode; onClick?: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }) : undefined}
      style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, background: 'var(--color-surface)', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--color-text-soft)', fontSize: 13, fontWeight: 600 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{icon} {title}</span>
        {onClick && <span style={{ fontSize: 11, color: 'var(--color-primary)' }}>Open →</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, margin: '6px 0 10px' }}>{big}</div>
      {children}
    </div>
  )
}
function Breakdown({ rows }: { rows: { key: string; count: number }[] }) {
  if (!rows.length) return <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>No data yet.</div>
  return <>{rows.map(r => <Row key={r.key} k={(r.key || '—').replace(/_/g, ' ')} v={r.count} />)}</>
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '2px 0', color: 'var(--color-text-soft)' }}><span style={{ textTransform: 'capitalize' }}>{k}</span><strong style={{ color: 'var(--color-text)' }}>{v}</strong></div>
}
